import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { X, ChevronLeft, ChevronRight, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';

import styles from './ModalImagePreview.module.scss';

const cx = classNames.bind(styles);

function ModalImagePreview({
  isOpen,
  images,
  initialIndex,
  currentIndex: propCurrentIndex,
  onClose,
  toggle,
}) {
  const activeIndex = initialIndex !== undefined ? initialIndex : (propCurrentIndex || 0);
  const handleClose = onClose || toggle || (() => {});
  const [currentIndex, setCurrentIndex] = useState(activeIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (isOpen) {
      const idx = initialIndex !== undefined ? initialIndex : (propCurrentIndex || 0);
      setCurrentIndex(idx >= 0 && idx < images.length ? idx : 0);
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen, initialIndex, propCurrentIndex, images.length]);

  const handlePrev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setZoom(1);
      setRotation(0);
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    },
    [images.length],
  );

  const handleNext = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setZoom(1);
      setRotation(0);
      setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    },
    [images.length],
  );

  const handleZoomIn = (e) => {
    if (e) e.stopPropagation();
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = (e) => {
    if (e) e.stopPropagation();
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = (e) => {
    if (e) e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  useEffect(() => {
    if (!isOpen) return () => {};

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, handleClose, handlePrev, handleNext]);

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex] || images[0];

  return createPortal(
    <div className={cx('ModalOverlay')} role="dialog" aria-modal="true">
      <button
        type="button"
        className={cx('Backdrop')}
        aria-label="Đóng popup xem ảnh"
        onClick={handleClose}
      />

      <div className={cx('HeaderToolbar')}>
        <div className={cx('ImageCounter')}>
          {`Ảnh ${currentIndex + 1} / ${images.length}`}
        </div>

        <div className={cx('ActionGroup')}>
          <button
            type="button"
            className={cx('ActionButton')}
            onClick={handleZoomIn}
            title="Phóng to"
            aria-label="Phóng to"
          >
            <ZoomIn size={18} />
          </button>
          <button
            type="button"
            className={cx('ActionButton')}
            onClick={handleZoomOut}
            title="Thu nhỏ"
            aria-label="Thu nhỏ"
          >
            <ZoomOut size={18} />
          </button>
          <button
            type="button"
            className={cx('ActionButton')}
            onClick={handleRotate}
            title="Xoay ảnh"
            aria-label="Xoay ảnh"
          >
            <RotateCw size={18} />
          </button>
          <button
            type="button"
            className={cx('ActionButton', 'CloseButton')}
            onClick={handleClose}
            title="Đóng"
            aria-label="Đóng"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className={cx('ContentWrapper')}>
        {images.length > 1 && (
          <button
            type="button"
            className={cx('NavButton', 'NavButtonPrev')}
            onClick={handlePrev}
            aria-label="Ảnh trước"
            title="Ảnh trước"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        <div
          className={cx('ImageContainer')}
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <img
            src={currentImage}
            alt={`Ảnh ${currentIndex + 1}`}
            className={cx('MainImage')}
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
            }}
          />
        </div>

        {images.length > 1 && (
          <button
            type="button"
            className={cx('NavButton', 'NavButtonNext')}
            onClick={handleNext}
            aria-label="Ảnh tiếp theo"
            title="Ảnh tiếp theo"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {images.length > 1 && (
        <div
          className={cx('ThumbnailStrip')}
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          {images.map((imgUrl, idx) => (
            <button
              type="button"
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              className={cx('ThumbnailItem', {
                Active: idx === currentIndex,
              })}
              onClick={() => {
                setZoom(1);
                setRotation(0);
                setCurrentIndex(idx);
              }}
              aria-label={`Xem ảnh ${idx + 1}`}
            >
              <img src={imgUrl} alt={`Thumbnail ${idx + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

ModalImagePreview.propTypes = {
  isOpen: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.string),
  initialIndex: PropTypes.number,
  currentIndex: PropTypes.number,
  onClose: PropTypes.func,
  toggle: PropTypes.func,
};

ModalImagePreview.defaultProps = {
  isOpen: false,
  images: [],
  initialIndex: 0,
  currentIndex: 0,
  onClose: () => {},
  toggle: () => {},
};

export default ModalImagePreview;
